import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, num, litres, formatDate, formatDateTime, todayISODate } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useCountry, countryInfo } from "../context/CountryContext";
import { Plus, X, GasPump, CheckCircle, XCircle, Truck as TruckIcon, ClipboardText, WarningCircle, Stack, ArrowRight, ClockCounterClockwise, FileText, UploadSimple, FileXls, PencilSimple, ArrowsCounterClockwise } from "@phosphor-icons/react";
import ExportButton from "../components/ExportButton";
import { TripTableHead, EMPTY_TRIP_FILTERS } from "../components/TripTableHead";
import { ReconcileCorrectionNote } from "../components/ReconcileCorrectionNote";

const ACTIVITY_TYPES = [
  { key: "loading",    label: "Loading",    hint: "Yard → out" },
  { key: "evacuation", label: "Evacuation", hint: "Containers from port (Arkas, Hapag Lloyd…)" },
  { key: "trip",       label: "Trip",       hint: "Point-to-point haul (Bunge, De Heus…)" },
  { key: "other",      label: "Other",      hint: "Anything else" },
];

const STATUS_CHIPS = {
  requested:         { label: "Requested",        cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved:          { label: "Approved",         cls: "bg-sky-50 text-ps-blue border-sky-200" },
  rejected:          { label: "Rejected",         cls: "bg-red-50 text-ps-red border-red-200" },
  issued:            { label: "Fuel issued",       cls: "bg-orange-50 text-ps-orange border-orange-200" },
  awaiting_waybill:  { label: "Awaiting waybill",  cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  completed:         { label: "Completed",         cls: "bg-lime-50 text-lime-700 border-lime-200" },
  cancelled:         { label: "Cancelled",         cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

const STATUS_FILTERS = ["", "requested", "approved", "issued", "awaiting_waybill", "completed", "rejected", "cancelled"];

/**
 * Shows on a cancelled activity: "→ 250 L carried to next trip (pending)" or "→ 250 L carried to Activity XYZ (De Heus · 2026-02-25)".
 * Fetches the fuel-credit row lazily so we know if it was already consumed by another approval.
 */
function CarriedFuelIndicator({ creditId, litresCarried, activityId }) {
  const [credit, setCredit] = useState(null);
  useEffect(() => {
    let cancelled = false;
    if (!creditId) return;
    api.get(`/fuel-credits/${creditId}`).then(({ data }) => {
      if (!cancelled) setCredit(data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [creditId]);
  if (!creditId || !litresCarried) return null;
  const applied = credit?.applied;
  const target = credit?.applied_to;
  return (
    <div className="mt-1 text-[10px] text-lime-700 max-w-[220px] flex items-start gap-1" data-testid={`activity-row-${activityId}-carried-fuel`}>
      <ArrowRight size={10} weight="bold" className="mt-[2px] shrink-0" />
      <div>
        <span className="font-mono-data font-bold">{litres(litresCarried)}</span>
        {applied && target ? (
          <> carried to <Link to={`/activities?id=${target.id}`} className="underline hover:text-lime-800 font-semibold">{target.client}</Link>{target.created_at && <> · {String(target.created_at).slice(0, 10)}</>}</>
        ) : (
          <> carried to next trip <span className="text-slate-400 italic">(pending)</span></>
        )}
      </div>
    </div>
  );
}

const EMPTY = (country = "GH") => ({
  date: todayISODate(),
  vehicle_number: "",
  driver_name: "",
  client: "",
  activity_type: "loading",
  activity_detail: "",
  fuel_requested_l: "",
  needs_waybill: false,
  country,
  notes: "",
  // Billing
  billing_mode: "",            // "" = inherit customer default
  tonnes_estimated: "",
  waiting_days_estimated: "",
  lane_id: "",                 // optional — selected from customer's lanes
  purchase_order_ref: "",      // PO# / project ref (for accounts dept)
});

export default function Activities() {
  const { user } = useAuth();
  const { scope, filterParam, defaultCountry, isAll, COUNTRIES } = useCountry();
  const canApprove = ["admin", "fuel_approver", "manager"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Initial status filter:
  //  - honor ?status= in the URL (e.g. coming from the Dashboard "Review now →" banner)
  //  - otherwise default fuel_approvers / approver-roles to "requested" so they immediately
  //    see Gilbert's pending requests when they land on the page.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = (() => {
    const s = searchParams.get("status") || "";
    if (STATUS_FILTERS.includes(s)) return s;
    if (["fuel_approver"].includes(user?.role)) return "requested";
    return "";
  })();
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [tripFilters, setTripFilters] = useState(EMPTY_TRIP_FILTERS);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => EMPTY(defaultCountry));
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // Excel import
  const [showImport, setShowImport] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importCountry, setImportCountry] = useState(() => (defaultCountry === "ALL" ? "TG" : defaultCountry || "TG"));
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importErr, setImportErr] = useState("");

  // Per-row inline approval state
  const [actionFor, setActionFor] = useState(null);            // {activity, mode: 'approve'|'reject'|'cancel'}
  const [approveQty, setApproveQty] = useState("");
  const [approvePrice, setApprovePrice] = useState("");
  const [approveLpt, setApproveLpt] = useState("10");
  const [approveSource, setApproveSource] = useState("yard");      // "yard" | "topup" | "mixed"
  const [yardLitres, setYardLitres] = useState("");                // for MIXED: yard-side portion (total = yard + topup)
  const [topupLitres, setTopupLitres] = useState("");
  const [topupPrice, setTopupPrice] = useState("");
  const [topupCurrency, setTopupCurrency] = useState("GHS");       // "GHS" | "XOF" — currency at the top-up station
  const [truckCredit, setTruckCredit] = useState(0);           // unapplied leftover fuel litres for the selected truck
  const [actionReason, setActionReason] = useState("");
  // Lanes for the currently-selected client (in the create modal)
  const [clientLanes, setClientLanes] = useState([]);
  // Trips reconciliation modal
  const [tripsActivity, setTripsActivity] = useState(null);
  const [tripsValue, setTripsValue] = useState("1");
  const [tonnesActualValue, setTonnesActualValue] = useState("");
  const [waitingActualValue, setWaitingActualValue] = useState("");
  const [prod, setProd] = useState(null);
  const [fuelHint, setFuelHint] = useState(null);
  // Track whether the user has manually edited the fuel field
  // (so we don't overwrite their value when the hint arrives).
  const [fuelTouched, setFuelTouched] = useState(false);

  // Pull a per-client/activity_type fuel suggestion whenever the user picks one
  useEffect(() => {
    if (!open) return;
    const client = (form.client || "").trim();
    if (!client) { setFuelHint(null); return; }
    let alive = true;
    const params = { client };
    if (form.activity_type) params.activity_type = form.activity_type;
    api.get("/activities/fuel-hint", { params })
      .then(({ data }) => {
        if (!alive) return;
        setFuelHint(data && data.trips_seen > 0 ? data : null);
        // Pre-fill if user hasn't typed anything in the fuel field
        if (data && data.trips_seen > 0 && !fuelTouched && !form.fuel_requested_l) {
          setForm((f) => ({ ...f, fuel_requested_l: String(data.suggestion_l) }));
        }
      })
      .catch(() => alive && setFuelHint(null));
    return () => { alive = false; };
    // eslint-disable-next-line
  }, [form.client, form.activity_type, open]);

  // Fetch the picked customer's lanes whenever the client changes
  useEffect(() => {
    if (!open) return;
    const clientName = (form.client || "").trim().toUpperCase();
    const cust = customers.find((c) => c.name === clientName);
    if (!cust) { setClientLanes([]); return; }
    let alive = true;
    api.get(`/customers/${cust.id}/lanes`)
      .then(({ data }) => { if (alive) setClientLanes(data || []); })
      .catch(() => alive && setClientLanes([]));
    return () => { alive = false; };
  }, [form.client, open, customers]);

  const loadProductivity = async () => {
    try {
      const params = filterParam ? { country: filterParam } : {};
      const { data } = await api.get("/activities/productivity", { params });
      setProd(data);
    } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (filterParam) params.country = filterParam;
    Object.entries(tripFilters).forEach(([k, v]) => { if (v) params[k] = v; });
    const [a, t, d, c] = await Promise.all([
      api.get("/activities", { params }),
      api.get("/trucks"),
      api.get("/drivers"),
      api.get("/customers"),
    ]);
    setRows(a.data);
    setTrucks(t.data);
    setDrivers(d.data);
    setCustomers(c.data);
    setLoading(false);
    loadProductivity();
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, filterParam, tripFilters]);

  // Deep-link focus: when arriving from Control Tower with ?focus={id}, scroll to that row
  // and apply a brief highlight ring so the operator immediately sees the trip in question.
  // Also supports ?action=new (open empty create modal) and ?action=more-fuel (open create
  // modal pre-filled from the focused activity — same flow as the row's "+ More fuel" button).
  const focusId = searchParams.get("focus");
  const actionParam = searchParams.get("action");
  useEffect(() => {
    if (loading) return;
    // ?action=new — open empty create modal regardless of focus
    if (actionParam === "new") {
      setForm(EMPTY(defaultCountry));
      setOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("action");
      setSearchParams(next, { replace: true });
      return;
    }
    if (rows.length === 0) return;
    if (focusId) {
      // ?action=more-fuel — pre-fill create modal from the focused row
      if (actionParam === "more-fuel") {
        const parent = rows.find((x) => x.id === focusId);
        if (parent) {
          requestMoreFuel(parent);
          const next = new URLSearchParams(searchParams);
          next.delete("action");
          next.delete("focus");
          setSearchParams(next, { replace: true });
          return;
        }
      }
      // Default: scroll-into-view with temporary highlight
      const t = setTimeout(() => {
        const el = document.querySelector(`[data-testid="activity-row-${focusId}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-ps-blue", "ring-offset-2", "bg-sky-50");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-ps-blue", "ring-offset-2", "bg-sky-50");
            const next = new URLSearchParams(searchParams);
            next.delete("focus");
            setSearchParams(next, { replace: true });
          }, 2500);
        }
      }, 80);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [focusId, actionParam, loading, rows]);

  // Trucks grouped by country (fleet origin)
  const fleetGroups = useMemo(() => {
    const groups = { GH: [], TG: [], BJ: [] };
    for (const tk of trucks) groups[tk.country || "GH"]?.push(tk);
    return groups;
  }, [trucks]);

  // Reset country default in form when scope changes (only when clean)
  useEffect(() => {
    if (!open) setForm((f) => ({ ...f, country: defaultCountry }));
    // eslint-disable-next-line
  }, [defaultCountry]);

  // When the truck changes, auto-pick the truck's country
  const pickTruck = (vehicle) => {
    const tk = trucks.find((x) => x.vehicle_number === vehicle);
    setForm((f) => ({ ...f, vehicle_number: vehicle, country: tk?.country || f.country }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSaving(true);
    try {
      await api.post("/activities", {
        ...form,
        date: new Date(form.date).toISOString(),
        fuel_requested_l: parseFloat(form.fuel_requested_l || 0),
        billing_mode:           form.billing_mode || null,
        tonnes_estimated:       form.tonnes_estimated       === "" ? null : parseFloat(form.tonnes_estimated),
        waiting_days_estimated: form.waiting_days_estimated === "" ? null : parseFloat(form.waiting_days_estimated),
        lane_id:                form.lane_id || null,
      });
      setOpen(false);
      setForm(EMPTY(defaultCountry));
      setFuelHint(null);
      setFuelTouched(false);
      await load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  const submitImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImportBusy(true);
    setImportErr("");
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(
        `/activities/import-xlsx?country=${importCountry}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setImportResult(data);
      // Refresh the list so freshly imported rows show up
      await load();
    } catch (e2) {
      setImportErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setImportBusy(false);
    }
  };

  const requestMoreFuel = (parent) => {
    // Pre-fill the New activity modal with same truck/client/driver/lane → operator just adjusts litres.
    setForm({
      date: todayISODate(),
      vehicle_number: parent.vehicle_number || "",
      driver_name: parent.driver_name || "",
      client: parent.client || "",
      activity_type: parent.activity_type || "loading",
      activity_detail: parent.activity_detail ? `${parent.activity_detail} · TOP-UP` : "TOP-UP",
      fuel_requested_l: "",
      needs_waybill: false,
      country: parent.country || defaultCountry,
      notes: `Additional fuel request for parent activity ${parent.id.slice(0,8)} (truck ${parent.vehicle_number})`,
      billing_mode: parent.billing_mode || "",
      tonnes_estimated: "",
      waiting_days_estimated: "",
      lane_id: parent.lane_id || "",
    });
    setOpen(true);
  };

  const startApprove = (act) => {
    setActionFor({ activity: act, mode: "approve" });
    setApproveQty(String(act.fuel_requested_l || ""));
    setApprovePrice("");
    // Smart lpt default: existing activity lpt → fuel_requested → 500 (tank fallback).
    // Resolved at submit-time on the backend if not explicitly entered, so just pre-fill what we already have.
    setApproveLpt(String(act.litres_per_trip && act.litres_per_trip > 10 ? act.litres_per_trip : (act.fuel_requested_l || 500)));
    setActionReason("");
    setTruckCredit(0);
    setApproveSource("yard");
    setYardLitres("");
    setTopupLitres("");
    setTopupPrice("");
    // Auto-pick currency from truck country (XOF for Togo/Benin, else GHS).
    const c = (act.country || "GH").toUpperCase();
    setTopupCurrency(c === "TG" || c === "BJ" ? "XOF" : "GHS");
    // Fetch any leftover fuel sitting on this truck so the approver knows it'll be auto-applied.
    if (act.vehicle_number) {
      api.get("/jobs/fuel-credits")
        .then(({ data }) => {
          const row = (data || []).find(r => r.vehicle_number === act.vehicle_number);
          setTruckCredit(row?.credit_l || 0);
        })
        .catch(() => setTruckCredit(0));
    }
  };
  const startReject = (act) => { setActionFor({ activity: act, mode: "reject" }); setActionReason(""); };
  const startCancel = (act) => { setActionFor({ activity: act, mode: "cancel" }); setActionReason(""); };

  const startTripsUpdate = (act) => {
    setTripsActivity(act);
    setTripsValue(String(act.trips_done || 1));
    setTonnesActualValue(act.tonnes_actual ? String(act.tonnes_actual) : (act.tonnes_estimated ? String(act.tonnes_estimated) : ""));
    setWaitingActualValue(act.waiting_days_actual ? String(act.waiting_days_actual) : (act.waiting_days_estimated ? String(act.waiting_days_estimated) : ""));
  };
  const submitTripsUpdate = async () => {
    if (!tripsActivity) return;
    const n = parseInt(tripsValue, 10);
    if (!n || n < 1) { window.alert("Trips must be at least 1"); return; }
    const payload = { trips_done: n };
    if (tonnesActualValue !== "") payload.tonnes_actual = parseFloat(tonnesActualValue);
    if (waitingActualValue !== "") payload.waiting_days_actual = parseFloat(waitingActualValue);
    try {
      await api.patch(`/activities/${tripsActivity.id}/trips-done`, payload);
      setTripsActivity(null);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doApprove = async () => {
    if (!actionFor) return;
    // MIXED mode: total = yard + topup (both editable side-by-side). Other modes: use approveQty directly.
    let qty;
    if (approveSource === "mixed") {
      const yl = parseFloat(yardLitres) || 0;
      const tl = parseFloat(topupLitres) || 0;
      qty = yl + tl;
      if (yl <= 0 || tl <= 0) { window.alert("For Mixed, both yard litres and top-up litres must be greater than zero. Use 'Yard tank' or 'Station top-up' if one side is empty."); return; }
    } else {
      qty = parseFloat(approveQty);
      if (!qty || qty <= 0) { window.alert("Approved litres must be greater than zero."); return; }
    }
    try {
      const payload = { fuel_approved_l: qty };
      if (approvePrice) payload.fuel_unit_price = parseFloat(approvePrice);
      const lpt = parseFloat(approveLpt);
      if (lpt && lpt > 0) payload.litres_per_trip = lpt;
      payload.fuel_source = approveSource;
      if (approveSource !== "yard") {
        // Source = topup OR mixed → both litres and price are required
        const tl = parseFloat(topupLitres);
        const tp = parseFloat(topupPrice);
        if (!tl || tl <= 0) { window.alert("Top-up litres must be greater than zero."); return; }
        if (!tp || tp <= 0) { window.alert("Top-up price/L must be greater than zero."); return; }
        if (tl > qty + 0.001) { window.alert(`Top-up (${tl} L) cannot exceed total (${qty} L).`); return; }
        payload.topup_litres = tl;
        payload.topup_price = tp;
        payload.topup_currency = topupCurrency || "GHS";
      }
      await api.post(`/activities/${actionFor.activity.id}/approve`, payload);
      setActionFor(null);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doReject = async () => {
    if (!actionFor) return;
    if (!actionReason.trim()) { window.alert("Please give a reason."); return; }
    try {
      await api.post(`/activities/${actionFor.activity.id}/reject`, { reason: actionReason.trim() });
      setActionFor(null);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doCancel = async () => {
    if (!actionFor) return;
    if (!actionReason.trim()) { window.alert("Please give a reason."); return; }
    try {
      await api.post(`/activities/${actionFor.activity.id}/cancel`, { reason: actionReason.trim() });
      setActionFor(null);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const markWaybillReceived = async (act) => {
    if (!window.confirm(`Mark waybill received for ${act.client} (${act.vehicle_number})?`)) return;
    try {
      await api.post(`/activities/${act.id}/waybill-received`);
      await load();
    } catch (e) {
      window.alert(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  // Counters per status (for top stat bar)
  const counts = STATUS_FILTERS.slice(1).reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s).length;
    return acc;
  }, {});
  const totalRequestedL = rows.filter((r) => r.status === "requested").reduce((s, r) => s + (r.fuel_requested_l || 0), 0);
  // Column filters run server-side (full history)
  const visibleRows = rows;
  const changeStatus = (s) => {
    setStatusFilter(s);
    const next = new URLSearchParams(searchParams);
    if (s) next.set("status", s); else next.delete("status");
    setSearchParams(next, { replace: true });
  };

  return (
    <div data-testid="activities-page">
      <PageHeader
        subtitle={isAll ? "Daily operations · All countries" : `Daily operations · ${COUNTRIES[scope].flag} ${COUNTRIES[scope].name}`}
        title="Trips"
        action={
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowImport(true); setImportResult(null); setImportFile(null); }}
              data-testid="activities-import-button"
              className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
            >
              <UploadSimple size={16} weight="bold" /> Import Excel
            </button>
            <ExportButton
              endpoint="activities/export"
              filenameBase={`trip-activities-${new Date().toISOString().slice(0, 7)}`}
              params={{
                status: statusFilter || undefined,
                country: filterParam || undefined,
                ...Object.fromEntries(Object.entries(tripFilters).filter(([, v]) => v)),
              }}
              testidPrefix="activities-export"
            />
            <button onClick={() => setOpen(true)} data-testid="new-activity-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
              <Plus size={16} weight="bold" /> New activity
            </button>
          </div>
        }
      />

      {/* Quick filter chips */}
      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex gap-2 items-center flex-wrap" data-testid="status-filter-row">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => changeStatus(s)}
            data-testid={`status-filter-${s || "all"}`}
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors uppercase tracking-wider font-bold ${
              statusFilter === s
                ? "bg-ps-blue text-white border-ps-blue shadow-sm"
                : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {s ? STATUS_CHIPS[s].label : "All"} {s && counts[s] > 0 ? <span className="ml-1 text-[10px] opacity-80">({counts[s]})</span> : null}
          </button>
        ))}
        <div className="ml-auto flex gap-6">
          <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Pending litres</div><div className="font-mono-data tabular text-xl text-amber-700">{litres(totalRequestedL)}</div></div>
          <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Total activities</div><div className="font-mono-data tabular text-xl text-ps-deep">{num(rows.length)}</div></div>
        </div>
      </div>

      {/* Productivity rollup — mirrors the Excel "Truck Productivity" / "Driver Productivity" sheets */}
      {prod && prod.total_trips > 0 && (
        <div className="px-8 py-5 bg-white border-b border-slate-200" data-testid="productivity-panel">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Monthly productivity</div>
              <div className="text-xl font-display font-extrabold tracking-tight text-ps-deep">
                {prod.total_trips} trips · {num(prod.total_fuel_l)} L
                <span className="text-sm text-slate-500 font-mono-data font-normal ml-2">
                  {new Date(prod.month + "-01").toLocaleDateString("en-GB", { month: "long", year: "numeric" })}
                </span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ProductivityList
              title="Trips per truck"
              testid="prod-by-truck"
              rows={prod.by_truck}
              keyField="vehicle_number"
              extra={(r) => `${num(r.fuel_l)} L`}
            />
            <ProductivityList
              title="Trips per driver"
              testid="prod-by-driver"
              rows={prod.by_driver.filter((r) => r.driver_name)}
              keyField="driver_name"
            />
          </div>
        </div>
      )}

      <div className="p-8">
        {Object.values(tripFilters).some(Boolean) && (
          <div className="mb-3 flex items-center gap-3 text-xs" data-testid="trip-filter-summary">
            <span className="text-slate-500 font-mono-data" data-testid="truck-filter-count">Showing {rows.length} filtered trip{rows.length === 1 ? "" : "s"}</span>
            <button type="button" onClick={() => setTripFilters(EMPTY_TRIP_FILTERS)} data-testid="filter-clear-all" className="text-ps-red font-semibold hover:underline">Clear column filters</button>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <TripTableHead
              isAll={isAll} filters={tripFilters} setFilters={setTripFilters}
              statusFilter={statusFilter} onStatusChange={changeStatus}
              statusOptions={STATUS_FILTERS} statusLabel={(s) => STATUS_CHIPS[s].label}
              activityTypes={ACTIVITY_TYPES} trucks={trucks} drivers={drivers} customers={customers} rows={rows}
            />
            <tbody className="divide-y divide-slate-100" data-testid="activities-table-body">
              {loading && <tr><td colSpan={isAll ? 11 : 10} className="p-6"><div className="indus-bar w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isAll ? 11 : 10} className="p-12 text-center text-slate-500" data-testid="trips-empty">
                  {Object.values(tripFilters).some(Boolean) || statusFilter ? "No trips match the current filters." : <>No trips yet. Click <span className="font-semibold text-ps-deep">New activity</span> to record a truck loading, evacuation or fuel request.</>}
                </td></tr>
              )}
              {!loading && visibleRows.length === 0 && rows.length > 0 && (
                <tr><td colSpan={isAll ? 11 : 10} className="p-8 text-center text-slate-500">No trips match the current filter.</td></tr>
              )}
              {visibleRows.map((r) => {
                const chip = STATUS_CHIPS[r.status] || STATUS_CHIPS.requested;
                return (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`activity-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono-data text-xs whitespace-nowrap">{formatDate(r.date)}</td>
                    {isAll && <td className="px-3 py-2 text-sm" title={countryInfo(r.country).name}>{countryInfo(r.country).flag}</td>}
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${chip.cls}`}>{chip.label}</span>
                      {(r.status === "cancelled" || r.status === "rejected") && (r.cancel_reason || r.rejection_reason) && (
                        <div
                          className="mt-1 text-[10px] text-slate-500 max-w-[200px]"
                          title={r.cancel_reason || r.rejection_reason}
                          data-testid={`activity-row-${r.id}-cancel-reason`}
                        >
                          <span className="font-semibold uppercase tracking-wider text-[9px] text-slate-400">
                            {r.status === "cancelled" ? "Cancelled" : "Rejected"}:
                          </span>{" "}
                          <span className="italic">{r.cancel_reason || r.rejection_reason}</span>
                          {(r.cancelled_by || r.rejected_by) && (
                            <div className="text-[9px] text-slate-400 mt-0.5">
                              by {(r.cancelled_by || r.rejected_by).split("@")[0]}
                              {r.cancelled_at && <> · {String(r.cancelled_at).slice(0, 10)}</>}
                            </div>
                          )}
                        </div>
                      )}
                      {r.status === "cancelled" && r.carried_fuel_credit_id && (
                        <CarriedFuelIndicator
                          creditId={r.carried_fuel_credit_id}
                          litresCarried={r.carried_fuel_credit_l}
                          activityId={r.id}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono-data text-ps-blue font-semibold">{r.vehicle_number}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{r.driver_name || "—"}</td>
                    <td className="px-3 py-2 font-semibold text-ps-deep truncate max-w-[180px]" title={r.client}>{r.client}</td>
                    <td className="px-3 py-2 text-xs">
                      <div className="capitalize font-semibold">{r.activity_type}</div>
                      {(r.origin || r.destination) && (
                        <div className="text-[11px] text-ps-blue font-semibold flex items-center gap-1 mt-0.5" title="Route">
                          <span className="truncate max-w-[80px]">{r.origin || "—"}</span>
                          <ArrowRight size={10} className="shrink-0" />
                          <span className="truncate max-w-[80px]">{r.destination || "—"}</span>
                        </div>
                      )}
                      {r.lane_name_snapshot && (
                        <div className="text-[10px] text-slate-500 truncate max-w-[200px]" title={r.lane_name_snapshot}>{r.lane_name_snapshot}</div>
                      )}
                      {r.purchase_order_ref && (
                        <div className="text-[10px] text-amber-700 font-mono-data truncate max-w-[200px]" title={`PO/Project: ${r.purchase_order_ref}`} data-testid={`activity-row-${r.id}-po-ref`}>
                          <span className="bg-amber-50 px-1 rounded border border-amber-200">PO</span> {r.purchase_order_ref}
                        </div>
                      )}
                      {r.activity_detail && <div className="text-slate-500 truncate max-w-[200px]" title={r.activity_detail}>{r.activity_detail}</div>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">
                      <div>{r.fuel_requested_l ? num(r.fuel_requested_l) : "—"}</div>
                      {r.fuel_approved_l > 0 && (
                        <div className="text-[10px] text-lime-700">
                          approved {num(r.fuel_approved_l)} · {ghs(r.fuel_total_ghs)}
                          {r.fuel_source && r.fuel_source !== "yard" && (() => {
                            const isCash = r.fuel_topup_kind === "cash_purchase";
                            const label = r.fuel_source === "mixed"
                              ? (isCash ? "Mixed · Cash" : "Mixed")
                              : (isCash ? "Cash" : "Station");
                            const cls = isCash
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-amber-100 text-ps-orange border-amber-200";
                            return <span className={`ml-1 px-1 py-0.5 rounded border text-[9px] font-bold uppercase tracking-wide ${cls}`} data-testid={`activity-row-${r.id}-fuel-source-chip`}>{label}</span>;
                          })()}
                        </div>
                      )}
                      {r.fuel_topup_l > 0 && (
                        <div className={`text-[10px] ${r.fuel_topup_kind === "cash_purchase" ? "text-emerald-700" : "text-ps-orange"}`} title={r.fuel_topup_kind === "cash_purchase" ? "Cash purchase (opportunistic buy outside yard)" : "Top-up bought at a fuel station"}>
                          {r.fuel_topup_kind === "cash_purchase" ? "cash" : "top-up"} {num(r.fuel_topup_l, 1)} L @ {ghs(r.fuel_topup_price)} = {ghs(r.fuel_topup_ghs)}
                        </div>
                      )}
                      {r.fuel_credit_applied_l > 0 && (
                        <div className="text-[10px] text-amber-700" title="Leftover fuel auto-applied from a previous job/activity">
                          credit −{num(r.fuel_credit_applied_l, 1)} L · yard {num(r.fuel_net_yard_l ?? (r.fuel_approved_l - r.fuel_credit_applied_l), 1)} L
                        </div>
                      )}
                      {r.fuel_credit_l > 0 && r.status === "completed" && (
                        <div className="text-[10px] text-amber-700" title="Unused fuel credited back to this truck for the next allocation">
                          +{num(r.fuel_credit_l, 1)} L credited back
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono-data tabular">
                      {(() => {
                        const planned = r.trips_planned || 1;
                        const done = r.trips_done || 1;
                        const reconcilable = ["completed", "awaiting_waybill", "issued"].includes(r.status);
                        const at_cap = done >= planned;
                        if (!reconcilable) {
                          return <span className="text-xs text-slate-300" title="Reconcilable after fuel is issued">—</span>;
                        }
                        // Single-trip jobs (loading / trip / typical evacuation) don't need a counter —
                        // just an unobtrusive edit pencil. Multi-trip evacuations get a slim progress chip.
                        if (planned <= 1) {
                          return (
                            <button
                              onClick={() => startTripsUpdate(r)}
                              data-testid={`activity-trips-${r.id}`}
                              title="Reconcile trip (mark done, set tonnes / wait days)"
                              className="group inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold text-slate-500 hover:text-ps-blue hover:bg-sky-50 transition-colors"
                            >
                              {at_cap
                                ? <span className="text-lime-700 font-bold" title="Reconciled">✓</span>
                                : <span className="text-slate-400">·</span>}
                              <PencilSimple size={10} weight="bold" className="opacity-60 group-hover:opacity-100" />
                            </button>
                          );
                        }
                        // Multi-trip evacuation — slim progress chip
                        const pct = Math.min(100, Math.round((done / planned) * 100));
                        return (
                          <button
                            onClick={() => startTripsUpdate(r)}
                            data-testid={`activity-trips-${r.id}`}
                            title={`${done} of ${planned} trips · click to update`}
                            className={`group inline-flex flex-col items-stretch gap-0.5 px-2 py-1 rounded-md border text-[11px] font-semibold transition-colors w-[68px] ${
                              at_cap
                                ? "border-lime-300 bg-lime-50 text-lime-700 hover:bg-lime-100"
                                : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-1 leading-tight">
                              <span className="tabular font-bold">{done}/{planned}</span>
                              <PencilSimple size={9} weight="bold" className="opacity-50 group-hover:opacity-100" />
                            </div>
                            <div className="h-[3px] bg-white/60 rounded-full overflow-hidden">
                              <div className={`h-full ${at_cap ? "bg-lime-500" : "bg-amber-500"}`} style={{ width: `${pct}%` }} />
                            </div>
                          </button>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2 text-center">{r.needs_waybill ? <FileText size={14} weight="bold" className="text-indigo-500 inline" title="Waybill required" /> : null}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex gap-1 justify-end items-center">
                        {r.status === "requested" && canApprove && (
                          <>
                            <button onClick={() => startApprove(r)} data-testid={`activity-approve-${r.id}`} title="Approve & issue fuel" className="border border-ps-blue text-ps-blue px-2 py-1 rounded text-xs font-bold hover:bg-sky-50 flex items-center gap-1">
                              <GasPump size={12} weight="bold" /> Approve
                            </button>
                            <button onClick={() => startReject(r)} data-testid={`activity-reject-${r.id}`} title="Reject" className="text-slate-400 hover:text-ps-red p-1"><XCircle size={14} /></button>
                          </>
                        )}
                        {r.status === "awaiting_waybill" && (
                          <button onClick={() => markWaybillReceived(r)} data-testid={`activity-waybill-${r.id}`} className="border border-indigo-500 text-indigo-600 px-2 py-1 rounded text-xs font-bold hover:bg-indigo-50 flex items-center gap-1">
                            <CheckCircle size={12} weight="bold" /> Waybill in
                          </button>
                        )}
                        {["issued", "awaiting_waybill", "completed"].includes(r.status) && (
                          <button onClick={() => requestMoreFuel(r)} data-testid={`activity-more-fuel-${r.id}`} title="Request additional fuel for this truck (e.g. mid-trip top-up)" className="border border-amber-400 text-amber-700 px-2 py-1 rounded text-xs font-bold hover:bg-amber-50 flex items-center gap-1">
                            <Plus size={12} weight="bold" /> More fuel
                          </button>
                        )}
                        {r.needs_waybill && r.status !== "cancelled" && r.status !== "rejected" && (
                          <Link
                            to={`/trips?from_activity=${r.id}`}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`activity-waybill-create-${r.id}`}
                            title="Open Waybill builder (CMR) pre-filled with this activity"
                            className="border border-indigo-300 text-indigo-700 px-2 py-1 rounded text-xs font-bold hover:bg-indigo-50 flex items-center gap-1"
                          >
                            <FileText size={12} weight="bold" /> Waybill
                          </Link>
                        )}
                        {(r.status === "requested" || r.status === "approved" || r.status === "issued" || r.status === "awaiting_waybill") && (
                          <button onClick={() => startCancel(r)} data-testid={`activity-cancel-${r.id}`} title="Cancel" className="text-slate-400 hover:text-ps-red p-1"><X size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Activity dialog */}
      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => { setOpen(false); setFuelHint(null); setFuelTouched(false); }} data-testid="activity-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Operations</div>
                <h2 className="font-heading font-black text-2xl tracking-tight">New activity</h2>
              </div>
              <button type="button" onClick={() => { setOpen(false); setFuelHint(null); setFuelTouched(false); }} className="text-slate-500"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-4">
              {/* Date */}
              <Field label="Date *" type="date" testid="activity-date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required />

              {/* Truck (grouped) */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Truck * <span className="text-slate-400 normal-case tracking-normal ml-1">(grouped by fleet origin)</span></label>
                <select
                  value={form.vehicle_number}
                  onChange={(e) => pickTruck(e.target.value)}
                  required
                  data-testid="activity-truck"
                  className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data bg-white"
                >
                  <option value="">— Select truck —</option>
                  {["GH", "TG", "BJ"].map((c) =>
                    fleetGroups[c]?.length > 0 ? (
                      <optgroup key={c} label={`${COUNTRIES[c].flag} ${COUNTRIES[c].name} fleet (${fleetGroups[c].length})`}>
                        {fleetGroups[c].map((tk) => (
                          <option key={tk.id} value={tk.vehicle_number}>{tk.vehicle_number}</option>
                        ))}
                      </optgroup>
                    ) : null
                  )}
                </select>
              </div>

              {/* Driver */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Driver</label>
                <input list="act-driver-list" value={form.driver_name} onChange={(e) => setForm({ ...form, driver_name: e.target.value })} data-testid="activity-driver" className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data" />
                <datalist id="act-driver-list">{drivers.map((d) => <option key={d.id} value={d.name} />)}</datalist>
              </div>

              {/* Client picker (linked to the address book) */}
              <ClientPicker
                value={form.client}
                onChange={(v) => {
                  // Auto-pick the customer's default activity type if the user hasn't customized it yet.
                  const upper = (v || "").trim().toUpperCase();
                  const cust = customers.find((c) => c.name === upper);
                  setForm((f) => {
                    const next = { ...f, client: v };
                    if (cust?.default_activity_type && f.activity_type === "loading" && cust.default_activity_type !== "loading") {
                      next.activity_type = cust.default_activity_type;
                    }
                    return next;
                  });
                }}
                customers={customers}
              />

              {/* Activity type */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Activity *</label>
                <div className="grid grid-cols-3 gap-2" data-testid="activity-type-toggle">
                  {ACTIVITY_TYPES.map((o) => {
                    const active = form.activity_type === o.key;
                    return (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => setForm({ ...form, activity_type: o.key })}
                        data-testid={`activity-type-${o.key}`}
                        className={`text-left border rounded-md px-3 py-2 text-sm transition-colors ${
                          active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <div className="font-semibold">{o.label}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <Field
                label="Activity detail (optional)"
                testid="activity-detail"
                value={form.activity_detail}
                onChange={(v) => setForm({ ...form, activity_detail: v })}
                placeholder="e.g. Containers Block A · 8 units, BINA Phase 2"
              />

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1 flex items-center justify-between">
                  <span>Fuel requested (litres)</span>
                  {fuelHint && (
                    <span className="text-[10px] normal-case tracking-normal text-ps-blue font-semibold" data-testid="fuel-hint-badge">
                      ~{fuelHint.suggestion_l} L based on {fuelHint.trips_seen} similar trip{fuelHint.trips_seen === 1 ? "" : "s"}
                    </span>
                  )}
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  value={form.fuel_requested_l}
                  onChange={(e) => { setFuelTouched(true); setForm({ ...form, fuel_requested_l: e.target.value }); }}
                  data-testid="activity-fuel"
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue"
                />
                {fuelHint && (
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2" data-testid="fuel-hint-detail">
                    last: <span className="font-mono-data text-ps-deep">{fuelHint.last_l} L</span>
                    <span>·</span>
                    range: <span className="font-mono-data text-ps-deep">{fuelHint.min_l} – {fuelHint.max_l} L</span>
                    <span>·</span>
                    median: <span className="font-mono-data text-ps-deep">{fuelHint.median_l} L</span>
                  </div>
                )}
              </div>

              {/* Service lane picker — when the client has lanes, picking one drives mode/rate/fuel */}
              {clientLanes.length > 0 && (
                <div className="border-t border-slate-200 pt-3" data-testid="activity-lane-picker">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold flex items-center gap-1">
                    <span>Service lane</span>
                    <span className="text-slate-400 normal-case tracking-normal font-normal">({clientLanes.length} available)</span>
                  </div>
                  <div className="space-y-1.5">
                    {clientLanes.map((lane) => {
                      const active = form.lane_id === lane.id;
                      return (
                        <button
                          key={lane.id}
                          type="button"
                          data-testid={`activity-lane-${lane.id}`}
                          onClick={() => setForm({
                            ...form,
                            lane_id: active ? "" : lane.id,
                            billing_mode: active ? "" : lane.billing_mode,
                            fuel_requested_l: (!active && lane.default_fuel_per_trip_l) ? String(lane.default_fuel_per_trip_l) : form.fuel_requested_l,
                          })}
                          className={`w-full text-left border rounded-md p-2 transition-colors ${active ? "border-ps-blue bg-sky-50" : "border-slate-200 hover:border-ps-blue hover:bg-sky-50/50"}`}
                        >
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`w-2 h-2 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                            <span className="font-semibold text-ps-deep text-sm">{lane.name}</span>
                            <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${lane.billing_mode === "per_tonne" ? "bg-amber-50 text-ps-orange border-amber-200" : "bg-sky-50 text-ps-blue border-sky-200"}`}>{lane.billing_mode === "per_tonne" ? "per tonne" : "per trip"}</span>
                          </div>
                          <div className="text-[11px] text-slate-500 mt-0.5 ml-4 font-mono-data">
                            {lane.rate_per_unit ? <>{lane.rate_per_unit} {lane.rate_currency} / {lane.billing_mode === "per_tonne" ? "t" : "trip"}</> : <span className="text-amber-700">no rate</span>}
                            {lane.default_fuel_per_trip_l ? <> · {lane.default_fuel_per_trip_l} L default</> : null}
                            {(lane.origin || lane.destination) ? <> · {[lane.origin, lane.destination].filter(Boolean).join(" → ")}</> : null}
                          </div>
                        </button>
                      );
                    })}
                    {form.lane_id && (
                      <button type="button" onClick={() => setForm({ ...form, lane_id: "", billing_mode: "" })} data-testid="activity-lane-clear" className="text-[11px] text-slate-500 hover:text-ps-deep underline ml-2">Clear lane selection (use customer default)</button>
                    )}
                  </div>
                </div>
              )}

              {/* Billing — pre-entered estimate, finalized at reconciliation */}
              {(() => {
                const cust = customers.find((c) => c.name === (form.client || "").trim().toUpperCase());
                const lane = clientLanes.find((l) => l.id === form.lane_id);
                const mode = form.billing_mode || lane?.billing_mode || cust?.default_billing_mode || "per_trip";
                const ccy = lane?.rate_currency || cust?.rate_currency || "GHS";
                const rate = lane ? lane.rate_per_unit : (mode === "per_tonne" ? cust?.rate_per_tonne : cust?.rate_per_trip);
                const wdRate = lane?.waiting_day_rate ?? cust?.waiting_day_rate;
                return (
                  <div className="border-t border-slate-200 pt-3" data-testid="activity-billing-section">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Billing (estimated)</div>
                    {!lane && (
                      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="activity-billing-mode-toggle">
                        {[
                          { v: "per_trip",  label: "Per trip" },
                          { v: "per_tonne", label: "Per tonne" },
                        ].map((o) => {
                          const active = mode === o.v;
                          return (
                            <button
                              key={o.v}
                              type="button"
                              onClick={() => setForm({ ...form, billing_mode: o.v })}
                              data-testid={`activity-billing-mode-${o.v}`}
                              className={`border rounded-md px-3 py-1.5 text-sm font-semibold transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                            >
                              {o.label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {(cust || lane) ? (
                      <div className="text-[11px] text-slate-600 mb-2" data-testid="activity-billing-rate-hint">
                        {rate ? <>Rate: <span className="font-mono-data font-semibold text-ps-deep">{rate} {ccy}</span> / {mode === "per_tonne" ? "tonne" : "trip"}{lane ? <span className="text-slate-400"> · from lane</span> : null}</> : <span className="text-amber-700">No rate set — add one on the Customer / Lane page or leave billing blank.</span>}
                        {wdRate ? <span> · waiting: <span className="font-mono-data font-semibold text-ps-deep">{wdRate} {ccy}</span> / day</span> : null}
                      </div>
                    ) : form.client ? (
                      <div className="text-[11px] text-amber-700 mb-2">Customer not in address book — save the activity, then add the rate to the customer.</div>
                    ) : null}
                    {mode === "per_tonne" && (
                      <Field label="Tonnes (estimated)" type="number" step="0.01" min="0" testid="activity-tonnes-estimated" value={form.tonnes_estimated} onChange={(v) => setForm({ ...form, tonnes_estimated: v })} placeholder="e.g. 25" />
                    )}
                    <div className="mt-2">
                      <Field label="Waiting days (estimated)" type="number" step="0.5" min="0" testid="activity-waiting-days-estimated" value={form.waiting_days_estimated} onChange={(v) => setForm({ ...form, waiting_days_estimated: v })} placeholder="0" />
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1">Final tonnes &amp; waiting days will be entered at reconciliation (after the trips are done).</div>
                  </div>
                );
              })()}

              {/* Needs waybill */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={form.needs_waybill} onChange={(e) => setForm({ ...form, needs_waybill: e.target.checked })} data-testid="activity-needs-waybill" className="mt-0.5" />
                <div>
                  <div className="text-sm font-semibold text-ps-deep">Waybill required for this job</div>
                  <div className="text-[11px] text-slate-500">Only tick this for one-off client delivery projects (not for BINA-type continuous work).</div>
                </div>
              </label>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">PO # / Project ref (optional)</label>
                <input
                  type="text"
                  value={form.purchase_order_ref}
                  onChange={(e) => setForm({ ...form, purchase_order_ref: e.target.value })}
                  data-testid="activity-purchase-order-ref"
                  className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data"
                  placeholder="e.g. PO-2026-0142 or 'Tema Port — Bunge Q2'"
                />
                <div className="text-[11px] text-slate-500 mt-1">Accounts uses this to link the fuel purchase to a specific job / order.</div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Notes</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="activity-notes"
                  className="w-full border border-slate-300 px-3 py-2 text-sm"
                />
              </div>

              {err && <div className="text-sm text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="activity-error">{err}</div>}
            </div>

            <div className="p-6 border-t border-slate-200 flex gap-2 bg-slate-50 sticky bottom-0">
              <button type="button" onClick={() => { setOpen(false); setFuelHint(null); setFuelTouched(false); }} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" disabled={saving} data-testid="activity-submit" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep disabled:opacity-60">
                {saving ? "Saving…" : "Submit request"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Approve / Reject / Cancel modal */}
      {actionFor && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 grid place-items-center p-4" onClick={() => setActionFor(null)} data-testid="action-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{actionFor.activity.client}</div>
              <h3 className="font-heading font-black text-xl tracking-tight">
                {actionFor.mode === "approve" && "Approve & issue fuel"}
                {actionFor.mode === "reject"  && "Reject request"}
                {actionFor.mode === "cancel"  && "Cancel activity"}
              </h3>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                <TruckIcon size={12} /> {actionFor.activity.vehicle_number}
                <span>·</span>
                <span>requested {num(actionFor.activity.fuel_requested_l)} L</span>
              </div>
            </div>

            <div className="p-5 space-y-3">
              {actionFor.mode === "approve" && (
                <>
                  {truckCredit > 0 && (() => {
                    const q = approveSource === "mixed"
                      ? (parseFloat(yardLitres) || 0) + (parseFloat(topupLitres) || 0)
                      : (parseFloat(approveQty) || 0);
                    const will = Math.min(q, truckCredit);
                    const fromYard = Math.max(0, q - will);
                    return (
                      <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 rounded" data-testid="approve-credit-banner">
                        <div className="font-semibold">
                          <span className="font-mono-data">{num(truckCredit, 1)} L</span> leftover fuel on truck {actionFor.activity.vehicle_number}
                        </div>
                        {q > 0 && (
                          <div className="text-amber-800 mt-0.5">
                            Will auto-deduct <span className="font-semibold font-mono-data">{num(will, 1)} L</span> from credit · only <span className="font-semibold font-mono-data">{num(fromYard, 1)} L</span> pulled from yard.
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {approveSource !== "mixed" && (
                    <Field label="Approved litres *" type="number" step="1" min="0" testid="approve-qty" value={approveQty} onChange={setApproveQty} />
                  )}
                  {/* Fuel source toggle — yard tank vs station top-up */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Fuel source</label>
                    <div className="grid grid-cols-3 gap-2" data-testid="approve-source-toggle">
                      {[
                        { v: "yard",  label: "Yard tank",  hint: "Deducts from yard stock" },
                        { v: "topup", label: "Station top-up", hint: "Bought on the road" },
                        { v: "mixed", label: "Mixed",      hint: "Part yard + part station" },
                      ].map((o) => {
                        const active = approveSource === o.v;
                        return (
                          <button
                            key={o.v}
                            type="button"
                            onClick={() => setApproveSource(o.v)}
                            data-testid={`approve-source-${o.v}`}
                            className={`text-left border rounded-md px-2 py-1.5 text-xs transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                          >
                            <div className="font-semibold flex items-center gap-1.5">
                              <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                              {o.label}
                            </div>
                            <div className="text-[9px] text-slate-500 mt-0.5">{o.hint}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {approveSource === "mixed" && (
                    <div className="space-y-2 border-l-2 border-ps-blue/40 bg-sky-50/50 px-3 py-2 rounded" data-testid="approve-mixed-block">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Yard + station split</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Yard litres *" type="number" step="0.1" min="0" testid="approve-yard-litres" value={yardLitres} onChange={setYardLitres} placeholder="e.g. 300" />
                        <Field label="Top-up litres *" type="number" step="0.1" min="0" testid="approve-mixed-topup-litres" value={topupLitres} onChange={setTopupLitres} placeholder="e.g. 1100" />
                      </div>
                      {(() => {
                        const yl = parseFloat(yardLitres) || 0;
                        const tl = parseFloat(topupLitres) || 0;
                        const total = yl + tl;
                        return total > 0 ? (
                          <div className="text-[11px] text-ps-deep mt-1" data-testid="approve-mixed-total">
                            → Total: <span className="font-mono-data font-bold">{num(total, 1)} L</span> <span className="text-slate-500">({num(yl, 1)} yard + {num(tl, 1)} top-up)</span>
                          </div>
                        ) : (
                          <div className="text-[11px] text-slate-500 mt-1">Enter yard and top-up litres — total is auto-computed.</div>
                        );
                      })()}
                      <Field label={`Top-up price / L (${topupCurrency}) *`} type="number" step="0.01" min="0" testid="approve-topup-price" value={topupPrice} onChange={setTopupPrice} placeholder="14.50" />
                      <div className="flex justify-end">
                        <ApproveCurrencyToggle value={topupCurrency} onChange={setTopupCurrency} testid="approve-topup-currency-mixed" />
                      </div>
                    </div>
                  )}
                  {approveSource === "topup" && (
                    <div className="space-y-2 border-l-2 border-ps-orange/40 bg-amber-50/40 px-3 py-2 rounded" data-testid="approve-topup-block">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-ps-orange font-semibold">Station top-up</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Top-up litres" type="number" step="0.1" min="0" testid="approve-topup-litres" value={topupLitres} onChange={setTopupLitres} placeholder="e.g. 50" />
                        <Field label={`Price / L (${topupCurrency})`} type="number" step="0.01" min="0" testid="approve-topup-price" value={topupPrice} onChange={setTopupPrice} placeholder="14.50" />
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] text-slate-600">
                          All approved litres come from the station — no yard stock is deducted.
                        </div>
                        <ApproveCurrencyToggle value={topupCurrency} onChange={setTopupCurrency} testid="approve-topup-currency" />
                      </div>
                    </div>
                  )}
                  <Field label="Yard unit price (GHS / L) — leave blank to use last recorded" type="number" step="0.01" min="0" testid="approve-price" value={approvePrice} onChange={setApprovePrice} />
                  <div>
                    <Field label="Litres per trip (caps the trip count)" type="number" step="1" min="1" testid="approve-lpt" value={approveLpt} onChange={setApproveLpt} />
                    {(() => {
                      const q = approveSource === "mixed"
                        ? (parseFloat(yardLitres) || 0) + (parseFloat(topupLitres) || 0)
                        : (parseFloat(approveQty) || 0);
                      const lpt = parseFloat(approveLpt) || 10;
                      const planned = Math.max(1, Math.floor(q / Math.max(lpt, 0.0001)));
                      return q > 0 ? (
                        <div className="text-[11px] text-slate-600 mt-1" data-testid="approve-trip-cap">
                          → cap of <span className="font-semibold text-ps-deep">{planned} trip{planned === 1 ? "" : "s"}</span> can be reconciled on this fuel issue.
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {approveSource === "topup"
                      ? "Issuing fuel will create a dispense record but yard stock is NOT reduced (station top-up)."
                      : approveSource === "mixed"
                        ? "Issuing fuel will create a dispense record. Only the yard portion reduces yard stock."
                        : "Issuing fuel will create a dispense record and reduce yard stock."}
                  </div>
                </>
              )}
              {actionFor.mode !== "approve" && (
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Reason *</label>
                  <textarea rows={3} value={actionReason} onChange={(e) => setActionReason(e.target.value)} data-testid="action-reason" className="w-full border border-slate-300 px-3 py-2 text-sm" />
                </div>
              )}
            </div>

            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2">
              <button onClick={() => setActionFor(null)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Close</button>
              {actionFor.mode === "approve" && <button onClick={doApprove} data-testid="approve-confirm" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep">Issue fuel</button>}
              {actionFor.mode === "reject"  && <button onClick={doReject}  data-testid="reject-confirm"  className="flex-1 bg-ps-red text-white py-2.5 text-sm font-semibold hover:bg-red-700">Reject</button>}
              {actionFor.mode === "cancel"  && <button onClick={doCancel}  data-testid="cancel-confirm"  className="flex-1 bg-slate-700 text-white py-2.5 text-sm font-semibold hover:bg-slate-800">Cancel activity</button>}
            </div>
          </div>
        </div>
      )}

      {/* Excel import modal */}
      {showImport && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          onClick={() => !importBusy && setShowImport(false)}
          data-testid="import-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-lg border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-ps-blue font-semibold">Bulk import</div>
                <h2 className="font-display font-extrabold text-xl text-ps-deep tracking-tight">Import activities from Excel</h2>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  French or English headers accepted: <span className="font-mono-data">Code camion · Date · Carburant · Trajet · Client</span>.
                  Rows are saved as <span className="font-semibold">completed</span> activities. Duplicates (same truck + date + fuel) are skipped automatically.
                </p>
              </div>
              <button type="button" onClick={() => !importBusy && setShowImport(false)} className="text-slate-400 hover:text-slate-700 ml-2"><X size={18} /></button>
            </div>

            <form onSubmit={submitImport} className="p-5 space-y-4">
              {/* Country selector */}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Country / fleet</label>
                <div className="flex gap-2" data-testid="import-country-row">
                  {[{ k: "TG", flag: "🇹🇬", l: "Togo" }, { k: "GH", flag: "🇬🇭", l: "Ghana" }, { k: "BJ", flag: "🇧🇯", l: "Benin" }].map((o) => (
                    <button
                      type="button"
                      key={o.k}
                      onClick={() => setImportCountry(o.k)}
                      data-testid={`import-country-${o.k}`}
                      className={`flex-1 px-3 py-2 rounded-md border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                        importCountry === o.k
                          ? "bg-ps-blue text-white border-ps-blue shadow-sm"
                          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                      }`}
                    >
                      <span>{o.flag}</span>{o.l}
                    </button>
                  ))}
                </div>
              </div>

              {/* File picker */}
              <div>
                <label className="block text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">Excel file</label>
                <label className={`flex items-center gap-3 border-2 border-dashed rounded-md px-4 py-5 cursor-pointer transition-colors ${importFile ? "border-ps-blue bg-ps-blue/5" : "border-slate-300 bg-slate-50 hover:bg-slate-100"}`}>
                  <FileXls size={28} weight="duotone" className="text-emerald-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    {importFile ? (
                      <>
                        <div className="text-sm font-semibold text-ps-deep truncate">{importFile.name}</div>
                        <div className="text-xs text-slate-500">{(importFile.size / 1024).toFixed(1)} KB · ready to import</div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-semibold text-ps-deep">Choose .xlsx file</div>
                        <div className="text-xs text-slate-500">Max ~5 MB</div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); setImportErr(""); }}
                    className="hidden"
                    data-testid="import-file-input"
                  />
                </label>
              </div>

              {importErr && (
                <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md" data-testid="import-error">{importErr}</div>
              )}

              {importResult && (
                <div className="border border-emerald-300 bg-emerald-50 rounded-md px-4 py-3 text-sm" data-testid="import-result">
                  <div className="flex items-center gap-2 text-emerald-900 font-semibold">
                    <CheckCircle size={16} weight="fill" className="text-emerald-600" />
                    {importResult.imported} imported · {importResult.skipped_duplicates} duplicate{importResult.skipped_duplicates === 1 ? "" : "s"} skipped · {importResult.errors?.length || 0} error{importResult.errors?.length === 1 ? "" : "s"}
                  </div>
                  <div className="text-xs text-emerald-800 mt-1">{importResult.total_rows_in_file} rows read from <span className="font-mono-data">{importResult.filename}</span>.</div>
                  {importResult.errors?.length > 0 && (
                    <ul className="mt-2 space-y-0.5 text-xs text-amber-900 max-h-32 overflow-y-auto bg-amber-50 border border-amber-200 rounded p-2" data-testid="import-error-list">
                      {importResult.errors.slice(0, 10).map((er, i) => (
                        <li key={i}>
                          <span className="font-mono-data">Row {er.row}</span>
                          {er.vehicle && <span className="font-mono-data text-ps-blue ml-2">{er.vehicle}</span>}
                          <span className="ml-2">— {er.error}</span>
                        </li>
                      ))}
                      {importResult.errors.length > 10 && <li className="italic">+ {importResult.errors.length - 10} more…</li>}
                    </ul>
                  )}
                </div>
              )}
            </form>

            <div className="px-5 py-4 border-t border-slate-200 flex gap-2 bg-slate-50 rounded-b-xl">
              <button
                type="button"
                onClick={() => !importBusy && setShowImport(false)}
                className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded-md text-slate-700 font-semibold"
              >
                Close
              </button>
              <button
                type="button"
                onClick={submitImport}
                disabled={!importFile || importBusy}
                data-testid="import-submit"
                className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold rounded-md hover:bg-ps-deep transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <UploadSimple size={16} weight="bold" />
                {importBusy ? "Importing…" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trips reconciliation modal */}
      {tripsActivity && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 grid place-items-center p-4"
          onClick={() => setTripsActivity(null)}
          data-testid="trips-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-sm border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Reconciliation</div>
              <h3 className="font-heading font-black text-xl tracking-tight text-ps-deep">Update trip count</h3>
              <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
                <TruckIcon size={12} weight="bold" className="text-ps-blue" />
                <span className="font-mono-data font-semibold text-ps-deep">{tripsActivity.vehicle_number}</span>
                <span>·</span>
                <span>{tripsActivity.client}</span>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <ReconcileCorrectionNote activity={tripsActivity} />
              <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Fuel issued</div>
                    <div className="font-mono-data text-sm font-semibold text-ps-deep">{num(tripsActivity.fuel_issued_l || tripsActivity.fuel_approved_l)} L</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">L / trip</div>
                    <div className="font-mono-data text-sm font-semibold text-ps-deep">{tripsActivity.litres_per_trip || 10}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Cap</div>
                    <div className="font-mono-data text-sm font-semibold text-lime-700">{tripsActivity.trips_planned || 1} trips</div>
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Trips actually completed</label>
                <input
                  type="number" min="1" max={tripsActivity.trips_planned || 1} step="1"
                  value={tripsValue}
                  onChange={(e) => setTripsValue(e.target.value)}
                  data-testid="trips-input"
                  autoFocus
                  className="w-full border border-slate-300 px-3 py-2 text-2xl font-mono-data tabular text-center font-bold text-ps-deep focus:border-ps-blue"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Must be between 1 and {tripsActivity.trips_planned || 1} (the fuel cap).
                </div>
              </div>

              {/* Billing — final tonnes + waiting days for invoicing */}
              {tripsActivity.rate_per_unit_snapshot > 0 || tripsActivity.waiting_day_rate_snapshot > 0 ? (
                <div className="border-t border-slate-200 pt-3" data-testid="trips-billing-section">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Billing reconciliation</div>
                  <div className="text-[11px] text-slate-600 mb-2">
                    Mode: <span className="font-semibold text-ps-deep">{tripsActivity.billing_mode === "per_tonne" ? "Per tonne" : "Per trip"}</span>
                    {tripsActivity.rate_per_unit_snapshot > 0 && (
                      <> · Rate: <span className="font-mono-data font-semibold text-ps-deep">{tripsActivity.rate_per_unit_snapshot} {tripsActivity.rate_currency_snapshot}</span> / {tripsActivity.billing_mode === "per_tonne" ? "tonne" : "trip"}</>
                    )}
                  </div>
                  {tripsActivity.billing_mode === "per_tonne" && (
                    <div className="mb-2">
                      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Tonnes actually carried</label>
                      <input
                        type="number" step="0.01" min="0"
                        value={tonnesActualValue}
                        onChange={(e) => setTonnesActualValue(e.target.value)}
                        data-testid="trips-tonnes-actual"
                        placeholder={tripsActivity.tonnes_estimated ? `estimate ${tripsActivity.tonnes_estimated} t` : "0"}
                        className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data focus:border-ps-blue"
                      />
                    </div>
                  )}
                  {tripsActivity.waiting_day_rate_snapshot > 0 && (
                    <div className="mb-2">
                      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Waiting days</label>
                      <input
                        type="number" step="0.5" min="0"
                        value={waitingActualValue}
                        onChange={(e) => setWaitingActualValue(e.target.value)}
                        data-testid="trips-waiting-actual"
                        placeholder="0"
                        className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data focus:border-ps-blue"
                      />
                    </div>
                  )}
                  {(() => {
                    const rate = parseFloat(tripsActivity.rate_per_unit_snapshot) || 0;
                    const wdr = parseFloat(tripsActivity.waiting_day_rate_snapshot) || 0;
                    const qty = tripsActivity.billing_mode === "per_tonne" ? parseFloat(tonnesActualValue || 0) : parseInt(tripsValue || 0, 10);
                    const wd = parseFloat(waitingActualValue || 0);
                    const total = rate * qty + wdr * wd;
                    return total > 0 ? (
                      <div className="bg-lime-50 border border-lime-200 rounded p-2 text-xs" data-testid="trips-billable-preview">
                        Billable: <span className="font-mono-data font-bold text-lime-700">{total.toFixed(2)} {tripsActivity.rate_currency_snapshot}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              ) : null}
            </div>
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2 rounded-b-xl">
              <button onClick={() => setTripsActivity(null)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded">Cancel</button>
              <button onClick={submitTripsUpdate} data-testid="trips-save" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function ApproveCurrencyToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex border border-slate-300 rounded overflow-hidden" data-testid={testid}>
      {["GHS", "XOF"].map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          data-testid={`${testid}-${c.toLowerCase()}`}
          className={`px-2.5 py-1.5 text-[11px] font-mono-data font-semibold tracking-wide ${value === c ? "bg-ps-blue text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue"
        {...rest}
      />
    </div>
  );
}

// --- Client picker linked to the customers address book ---
// Type-ahead combobox; lets you free-type a new client (which the backend auto-saves
// into the address book on activity creation).
function ClientPicker({ value, onChange, customers }) {
  const [openList, setOpenList] = useState(false);
  const q = (value || "").trim().toLowerCase();
  const matches = (q ? customers.filter((c) => c.name.toLowerCase().includes(q)) : customers).slice(0, 12);
  const exact = customers.find((c) => c.name.toLowerCase() === q);
  const isNew = q && !exact;

  return (
    <div className="relative" data-testid="activity-client-picker">
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1 flex items-center justify-between">
        <span>Client *</span>
        {value && (
          exact
            ? <span className="text-[10px] normal-case tracking-normal text-lime-700 font-semibold">✓ in address book</span>
            : <span className="text-[10px] normal-case tracking-normal text-amber-700">new client — will be saved</span>
        )}
      </label>
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpenList(true); }}
        onFocus={() => setOpenList(true)}
        onBlur={() => setTimeout(() => setOpenList(false), 150)}
        required
        data-testid="activity-client"
        placeholder="Type to search or add a new client…"
        className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue"
        autoComplete="off"
      />
      {openList && (matches.length > 0 || isNew) && (
        <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
          {matches.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => {                      // mouseDown beats blur
                e.preventDefault();
                onChange(c.name);
                setOpenList(false);
              }}
              data-testid={`activity-client-option-${c.id}`}
              className="w-full text-left px-3 py-2 hover:bg-sky-50 border-b border-slate-100 last:border-b-0"
            >
              <div className="font-semibold text-ps-deep text-sm flex items-center gap-2">
                {c.name}
                <span className="ml-auto text-[10px] text-slate-400 font-normal">used {c.usage_count}×</span>
              </div>
              {c.address && <div className="text-xs text-slate-500 truncate">{c.address}</div>}
            </button>
          ))}
          {isNew && (
            <div className="px-3 py-2 text-xs bg-amber-50 text-amber-800 border-t border-amber-100" data-testid="activity-client-new-hint">
              ↩ Press <kbd className="font-mono">Enter</kbd> or just submit — <span className="font-semibold">{value}</span> will be added to the address book.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProductivityList({ title, testid, rows, keyField, extra }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-slate-200 rounded-md bg-slate-50 p-4 text-sm text-slate-500 italic" data-testid={`${testid}-empty`}>
        {title}: no data yet for this month.
      </div>
    );
  }
  const max = rows[0]?.trips || 1;
  return (
    <div className="border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-semibold">{title}</div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">{rows.length} entries</div>
      </div>
      <div className="divide-y divide-slate-100 max-h-72 overflow-auto">
        {rows.map((r) => {
          const width = `${Math.max(6, Math.round((r.trips / max) * 100))}%`;
          return (
            <div key={r[keyField] || "—"} className="px-4 py-2 flex items-center gap-3 hover:bg-slate-50">
              <div className="font-mono-data text-sm text-ps-deep font-semibold truncate min-w-[120px]">{r[keyField] || "—"}</div>
              <div className="flex-1 relative h-2 bg-slate-100 rounded-sm overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-ps-blue" style={{ width }} />
              </div>
              <div className="font-mono-data tabular text-sm text-ps-deep font-bold min-w-[28px] text-right">{r.trips}</div>
              {extra && <div className="text-[10px] text-slate-500 font-mono-data min-w-[60px] text-right">{extra(r)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}


